import { createPkcePair, createState } from './pkce.ts'

export interface AuthorizationServer {
  readonly authorizationEndpoint: string
  readonly clientId: string
  readonly redirectUri: string
  readonly scopes: readonly string[]
}

export interface AuthorizationRequest {
  /** Where to send the browser. */
  readonly url: string
  /** Held until the callback returns; both are checked before any code is exchanged. */
  readonly verifier: string
  readonly state: string
}

/**
 * Authorization code with PKCE. The browser is a public client and holds no secret; the
 * code it receives is worthless without the verifier, and the verifier never crosses to
 * the authorization server.
 */
export async function createAuthorizationRequest(server: AuthorizationServer): Promise<AuthorizationRequest> {
  const pkce = await createPkcePair()
  const state = createState()
  const parameters = new URLSearchParams({
    client_id: server.clientId,
    redirect_uri: server.redirectUri,
    response_type: 'code',
    scope: server.scopes.join(' '),
    code_challenge: pkce.challenge,
    code_challenge_method: pkce.method,
    state,
    // Asks Google for a refresh token, which the backend keeps: the browser never holds one.
    access_type: 'offline',
    // Google only issues a refresh token on a user's first consent for this client+scope;
    // forcing re-consent on every sign-in guarantees one every time, not just the first.
    prompt: 'consent',
    include_granted_scopes: 'true',
  })
  return {
    url: `${server.authorizationEndpoint}?${parameters.toString()}`,
    verifier: pkce.verifier,
    state,
  }
}

export type CallbackOutcome =
  | { readonly kind: 'code'; readonly code: string; readonly state: string }
  | { readonly kind: 'declined'; readonly error: string; readonly description?: string }
  | { readonly kind: 'unusable'; readonly reason: 'no-code' | 'no-state' }

/** Reads what the authorization server sent back. Nothing here trusts it yet. */
export function readCallback(url: string): CallbackOutcome {
  const parameters = new URL(url).searchParams
  const error = parameters.get('error')
  if (error !== null) {
    const description = parameters.get('error_description')
    return { kind: 'declined', error, ...(description === null ? {} : { description }) }
  }
  const code = parameters.get('code')
  const state = parameters.get('state')
  if (code === null) return { kind: 'unusable', reason: 'no-code' }
  if (state === null) return { kind: 'unusable', reason: 'no-state' }
  return { kind: 'code', code, state }
}
